<template>
	<v-card class="mx-auto" elevation="0">
		<v-card-title class="card-header text-left fixed-card-title2" v-if="!wizardMode">
			<v-row>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('add-staff') }}</v-toolbar-title>
				</v-col>
				<v-col cols="2" class="text-right mt-n1">
					<v-icon color="white" @click="closeDrawer">mdi mdi-close</v-icon>
				</v-col>
			</v-row>
		</v-card-title>
		<!-- Role -->
		<v-card-text class="pa-2">
			<v-form ref="form" v-model="valid" lazy-validation>
				<v-row dense>
					<v-col cols="6">
						<v-text-field v-model="firstName" hide-details outlined dense :label="$t('name')" required />
					</v-col>
					<v-col cols="6">
						<v-text-field v-model="lastName" outlined dense hide-details :label="$t('surname')" required />
					</v-col>
					<v-col cols="6">
						<v-text-field
							v-model="email"
							outlined
							dense
							hide-details
							:label="$t('email')"
							:rules="[(v) => !!v || $t(''), (v) => /.+@.+\..+/.test(v) || $t('validEmail')]"
							required>
						</v-text-field>
					</v-col>
					<v-col cols="6">
						<v-text-field disabled outlined dense hide-details v-model="phone" :label="$t('phone')" required />
					</v-col>
					<v-col cols="12" class="text-left">
						<span class="font-weight-bold">{{$t('email-hint-caregiver')}}</span>
					</v-col>
					<v-col cols="12">
						<v-autocomplete
							class="width-text-field2"
							disabled
							hide-details
							dense
							v-model="selectedHospitalId"
							:items="filteredHospitals()"
							item-value="id"
							item-text="hospitalName"
							:label="$t('hospital')"
							outlined
							required
							@change="setSelectedHospital" />
					</v-col>
					<v-col cols="12">
						<v-select
							:label="$t('role')"
							disabled
							outlined
							required
							hide-details
							dense
							:item-text="this.$i18n.locale === 'en' ? 'roleNameForDisplay' : 'roleNameForDisplayESP'"
							item-value="id"
							v-model="role"
							:items="roles"
							:menu-props="{ offsetY: true }" />
					</v-col>
					<v-col cols="12" class="text-left">
						<span class="color-primary">{{ $t('cannot-edit-password-here') }}</span>
					</v-col>
					<v-col v-if="!addressIsValid" cols="12">
						<v-combobox
							:label="$t('time-zone')"
							dense
							hide-details
							outlined
							:rules="[(v) => !!v || $t('timeZoneSelect')]"
							v-model="timezone"
							item-text="timeZoneId"
							item-value="timeZoneId"
							:items="getTimeZone"
							required />
					</v-col>
				</v-row>
				<!-- Divider -->
				<v-row v-if="role === 3">
					<v-divider class="mx-1 my-3" />
				</v-row>
				<v-row class="my-0 py-0" v-if="[3, 4].includes(role)">
					<v-col class="my-0 py-0">
						<v-checkbox :label="$t('can-assign-rpm-rtm-ccm')" v-model="isSequoiaUser" />
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-container class="px-4" v-if="!wizardMode">
			<v-row dense>
				<v-col cols="6">
					<v-btn @click="closeDrawer" color="secondary" style="color: black" block>
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-col cols="6">
					<v-btn
						:disabled="
							!valid ||
							!role ||
							!firstName?.trim() ||
							!lastName?.trim() ||
							timezone === null ||
							!email.trim() ||
							!phone.trim() ||
							selectedHospitalId === null
						"
						color="dark_purple"
						style="color: white"
						@click="saveStaff"
						block>
						{{ $t('save') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { showSuccessAlert, showErrorAlert } from '@/utils/utilities';
export default {
	name: 'DashboardCoreAppBar',
	components: {},
	props: {
		wizardMode: {
			type: Boolean,
			default: false,
		},
		wizardData: {
			type: Object,
			default: () => ({}),
		},
	},
	data: () => ({
		valid: true,
		timezone: null,
		imageSrc: null,
		specialization: null,
		role: 7,
		firstName: '',
		lastName: '',
		phone: '+1 234 567 8900',
		phoneRules: [],
		email: '',
		selectedHospitalId: null,
		selectedHospital: null,
		addressIsValid: true,
		isSequoiaUser: false,
	}),
	async mounted () {
		this.$store.dispatch('verifiers/getTimeZones');
		await this.getAllHospitals();
	},

	computed: {
		...mapState({
			hospitals: (state) => state.hospitals.hospitals,
			userData: (state) => state.authentication.userData,
		}),
		...mapGetters({
			roles: 'roles/getRolesForForm',
			getSpecializationsForForm: 'verifiers/getSpecializationsForForm',
			getTimeZone: 'verifiers/getTimeZone',
			roleName: 'authentication/getRole',
		}),
		getHospital: {
			get () {
				// return this.userData.hospitalAssignedData;
				const selectedHospital =
					this.selectedHospitalId == null ? null : this.hospitals.find((h) => h.id === this.selectedHospitalId);
				return selectedHospital;
			},
		},
	},
	methods: {
		filteredHospitals () {
			if (this.wizardMode) {
				this.selectedHospitalId = this.wizardData.id;
				return [this.wizardData];
			}

			if (this.hospitals == null) {
				return [];
			}
			if (this.userData.roleData.roleName === 'DOCTOR' || this.userData.roleData.roleName === 'NURSE') {
				const hospitalIdFromLocalStorage = localStorage.getItem('selectedHospitalAssignedId');
				return this.hospitals.filter((h) => h.id === Number(hospitalIdFromLocalStorage));
			}
			return this.hospitals;
		},
		setSelectedHospital (val) {
			this.timezone = null;
			this.selectedHospital = this.hospitals.find((hospital) => hospital.id === val);

			if (
				this.selectedHospital.addressLatitude !== 0.01 &&
				this.selectedHospital.addressLongitude !== 0.01 &&
				this.selectedHospital.addressLongitude !== null &&
				this.selectedHospital.addressLatitude !== null
			) {
				this.addressIsValid = true;

				const latLng = {
					lat: this.selectedHospital.addressLatitude,
					lng: this.selectedHospital.addressLongitude,
				};

				this.$store.dispatch('googleAPI/getTimeZone', latLng).then((response) => {
					this.timezone = response.zoneName;
				});
			} else {
				// TODO There is bad address show time zoene lookup
				this.addressIsValid = false;
				this.selectedHospital.addressLatitude = 0.01;
				this.selectedHospital.addressLongitude = 0.01;
			}
		},
		async getAllHospitals () {
			// we call this API endpoint only if the store values are null
			if (this.hospitals == null) {
				await this.$store.dispatch('hospitals/getHospitals').then(() => {
					// this.loading = false;
				});
			}
		},
		async getStaff () {
			// this.loading = true;
			if (this.roleName === 'ORGANIZATION_ADMIN') {
				const organizationId = this.userData.organizationId;
				await this.$store
					.dispatch('medicalInstitutions/getRelatedOrganizationMedicalStaff', organizationId)
					.then(() => {
						// this.loading = false;
					});
			} else if (this.roleName === 'HOSPITAL_ADMIN' || this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
				const hospitalId = localStorage.getItem('selectedHospitalAssignedId');
				await this.$store.dispatch('hospitals/getRelatedMedicalStaff', hospitalId).then(() => {
					// this.loading = false;
				});
			}
		},
		async saveStaff () {
			if (this.$refs.form.validate()) {
				const body = {
					firstName: this.firstName,
					lastName: this.lastName,
					phone: this.phone.split(' ').join(''),
					email: this.email,
					address: this.getHospital.address,
					zip: this.getHospital.zip,
					city: this.getHospital.city,
					state: this.getHospital.state,
					country: this.getHospital.country,
					addressLatitude: this.getHospital.addressLatitude,
					addressLongitude: this.getHospital.addressLongitude,
					username: this.email,
					// password: '123456789',
					roleId: this.role,
					hospitalId: this.getHospital.id,
					userTimezone: this.addressIsValid ? this.timezone : this.timezone.timeZoneId,
					isSequoiaUser: this.isSequoiaUser,
				};
				const createUserResponse = await this.$store.dispatch('users/createUser', body);
				if (createUserResponse.resFlag === true) {
					if (this.role === 3) {
						const createdDoctorUserId = createUserResponse.data.id;

						var specializationIds = [];
						if (this.specialization !== null && this.specialization.length > 0) {
							this.specialization.forEach((id) => {
								specializationIds.push(id);
							});
						}

						if (specializationIds.length > 0) {
							this.$store
								.dispatch('doctormetadata/assignDoctorToSpetialization', {
									doctorUserId: createdDoctorUserId,
									spetializationTypeIdsList: specializationIds,
								})
								.then((res) => {
									// this.$emit('updated', res);
								});
						}
					}
					this.addressIsValid = true;
					this.getStaff();
					showSuccessAlert(this.$t('successfully-created-member'));

					if (this.wizardMode && this.wizardData) {
						this.$emit('update:addStaffResponse', createUserResponse.data);
						return createUserResponse;
					}

					this.closeDrawer();
				} else if (createUserResponse.resFlag === false) {
					showErrorAlert(createUserResponse.msg);
				}

				if (this.wizardMode && this.wizardData) {
					return createUserResponse;
				}
			} else {
				this.$refs.form.validate();
				showErrorAlert(this.$t('failed!'));
			}
		},
		onFileChange (event) {
			const file = event[0];
			const reader = new FileReader();
			reader.onload = (e) => {
				this.imageSrc = e.target.result;
			};
			reader.readAsDataURL(file);
		},
		closeDrawer () {
			this.firstName = '';
			this.email = '';
			this.phone = '+1';
			this.timezone = '';
			this.lastName = '';
			this.role = '';
			this.selectedHospitalId = '';
			this.fileUpload = null;
			this.imageSrc = null;
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		validateEmail (email) {
			const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return regex.test(email);
		},
	},
	watch: {

		// email FORMAT
		firstName (firstName) {
			this.$emit('update:firstName', firstName);
		},
		lastName (lastName) {
			this.$emit('update:lastName', lastName);
		},
		email (email) {
			this.$emit('update:email', email);
			this.$emit('update:validateEmail', this.validateEmail(email));
		},
		// if the role has changed and is not doctor and nurse, set the isSequoiaUser value to false;
		role (val) {
			if (val !== 3 && val !== 4) {
				this.isSequoiaUser = false;
			}
		},
	},
};
</script>
